import axios from "axios";
import endpoints, { API_URL } from "../utils/endpoints";
import { accessToken } from "../utils/token";
import fileDownload from "js-file-download";
import Cookies from "js-cookie";

export interface PublicContractDetail {
  contract_id: number;
  name: string;
  dob?: string;
  pob?: string;
  last_education?: string;
  gender?: string;
  religion?: string;
  address?: string;
  id_number?: string;
  email?: string;
  phone?: string;
  branch: string;
  branch_address?: string;
  placement_location: string;
  salary?: string;
  allowance_health?: string;
  allowance_operational?: string;
  contract_start: string;
  contract_end: string;
  is_probation?: boolean;
  status: string;
  token_used: boolean;
  preview_url?: string;
}

const getAllContracts = async (params: any) => {
  const url = endpoints.CONTRACT;
  const { data: response } = await axios.get(url, {
    headers: {
      Authorization: accessToken,
    },
    params: params,
  });
  return response;
}

const getEmployeeContracts = async (id: any) => {
  const url = endpoints.GET_EMPLOYEE_CONTRACT(id);

  const { data: response } = await axios.get(url, {
    headers: {
      Authorization: accessToken,
    },
  });

  return response;
};

const viewContractDetail = async (id: any) => {
  if (parseInt(id)) {
    const url = endpoints.CONTRACT + "/" + id;
    const { data: response } = await axios.get(url, {
      headers: {
        Authorization: accessToken,
      },
    });
    return response;
  }
};

const createContract = async (params: any) => {
  const url = endpoints.CONTRACT;
  const { data: response } = await axios.post(url, params, {
    headers: {
      Authorization: accessToken,
    },
  });

  return response;
};

const deleteContract = async (id: number) => {
  const url = endpoints.CONTRACT + "/" + id;

  const { data: response } = await axios.delete(url, {
    headers: {
      Authorization: accessToken,
    },
  });
  return response;
};

const updateContract = async (params: any) => {
  const url = endpoints.CONTRACT + "/" + params.id;

  const { data: response } = await axios.patch(url, params, {
    headers: {
      Authorization: accessToken,
    },
  });
  return response;
};

// Update draft contract (HR updates after employee rejection)
const updateDraftContract = async (params: any) => {
  const url = `${endpoints.CONTRACT}/${params.id}/draft`;

  const { data: response } = await axios.put(url, params, {
    headers: {
      Authorization: accessToken,
    },
  });
  return response;
};

// Employee approves contract
const employeeApproveContract = async (params: { id: number; employee_id: number, last_updated_by: string }) => {
  const url = `${endpoints.CONTRACT}/${params.id}/employee-approve`;

  const { data: response } = await axios.post(url, {
    employee_id: params.employee_id,
    last_updated_by: params.last_updated_by
  }, {
    headers: {
      Authorization: accessToken,
    },
  });

  return { ...response, action: 'approve' };
};

// Employee rejects contract
const employeeRejectContract = async (params: {
  id: number;
  employee_id: number;
  rejection_reason: string;
  last_updated_by: string;
}) => {
  const url = `${endpoints.CONTRACT}/${params.id}/employee-reject`;

  const { data: response } = await axios.post(url, {
    employee_id: params.employee_id,
    rejection_reason: params.rejection_reason,
    last_updated_by: params.last_updated_by
  }, {
    headers: {
      Authorization: accessToken,
    },
  });

  return { ...response, action: 'reject' };
};

// HR approves contract
const hrApproveContract = async (params: {
  id: number;
  hr_id: number;
  last_updated_by: string;
  role_id: number;
}) => {
  const url = `${endpoints.CONTRACT}/${params.id}/hr-approve`;

  const { data: response } = await axios.post(url, {
    hr_id: params.hr_id,
    last_updated_by: params.last_updated_by,
    role_id: params.role_id
  }, {
    headers: {
      Authorization: accessToken,
    },
  });

  return { ...response, action: 'approve' };
};

// HR rejects contract
const hrRejectContract = async (params: {
  id: number;
  hr_id: number;
  rejection_reason: string;
  last_updated_by: string;
  role_id: number;
}) => {
  const url = `${endpoints.CONTRACT}/${params.id}/hr-reject`;

  const { data: response } = await axios.post(url, {
    hr_id: params.hr_id,
    rejection_reason: params.rejection_reason,
    last_updated_by: params.last_updated_by,
    role_id: params.role_id
  }, {
    headers: {
      Authorization: accessToken,
    },
  });

  return { ...response, action: 'reject' };
};

// Download contract as DOCX
const downloadContract = async (contractId: number) => {
  const url = endpoints.CONTRACT_DOWNLOAD;
  await axios.post(url, { contractId }, {
    headers: {
      Authorization: accessToken,
      ContentType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    },
    responseType: 'arraybuffer',
  }).then((res) => {
    const contentDisposition = res.headers['content-disposition'];
    const filename = contentDisposition?.split('filename=')[1] || `Contract-${contractId}.docx`;
    var blob = new Blob([res.data], {
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    });
    fileDownload(blob, filename);
  });
};

// Generic method to respond to contracts (used in older implementations)
const respondToContract = async (params: { id: number, action: 'approve' | 'reject' }) => {
  const url = endpoints.CONTRACT_RESPOND;
  const { data: response } = await axios.post(url, params, {
    headers: {
      Authorization: accessToken,
    },
  });

  return response;
};

// Upload signed contract (employee uploads signed PDF)
const uploadSignedContract = async (id: number, file: File) => {
  const url = `${endpoints.CONTRACT}/${id}/upload-signed`;
  const formData = new FormData();
  formData.append("file", file);

  // Add employee data from cookies
  const user = JSON.parse(Cookies.get("USER") || "{}");
  formData.append("employee_id", user.id);
  formData.append("last_updated_by", user.name || user.username);

  const { data: response } = await axios.post(url, formData, {
    headers: {
      Authorization: accessToken,
      "Content-Type": "multipart/form-data",
    },
  });
  return response;
};

// HR uploads contract document
const uploadHrDocument = async (id: number, file: File) => {
  const url = `${endpoints.CONTRACT}/${id}/upload-hr-document`;
  const formData = new FormData();
  formData.append("file", file);

  // Add HR data from cookies
  const user = JSON.parse(Cookies.get("USER") || "{}");
  formData.append("hr_id", user.id);
  formData.append("last_updated_by", user.name || user.username);
  formData.append("role_id", user.role_id);

  const { data: response } = await axios.post(url, formData, {
    headers: {
      Authorization: accessToken,
      "Content-Type": "multipart/form-data",
    },
  });
  return response;
};

// Get contract history
const getContractHistory = async (contractId: number) => {
  const url = endpoints.CONTRACT_HISTORY(contractId);
  const { data: response } = await axios.get(url, {
    headers: {
      Authorization: accessToken,
    },
  });
  return response;
};

// Download history document as DOCX
const downloadHistoryDocument = async (params: { documentId: number, documentPath: string }) => {
  const url = endpoints.CONTRACT_DOWNLOAD;
  const payload = { filePath: params.documentPath };

  await axios.post(url, payload, {
    headers: {
      Authorization: accessToken,
      ContentType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    },
    responseType: 'arraybuffer',
  }).then((res) => {
    const contentDisposition = res.headers['content-disposition'];
    const filename = contentDisposition?.split('filename=')[1] || `Contract-${params.documentId}.docx`;
    var blob = new Blob([res.data], {
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    });
    fileDownload(blob, filename);
  });
};

// Download current contract template
const downloadContractTemplate = async () => {
  const url = endpoints.CONTRACT + "/download";

  await axios.post(url, { filePath: "Template.docx" }, {
    headers: {
      Authorization: accessToken,
      ContentType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    },
    responseType: 'arraybuffer',
  }).then((res) => {
    const contentDisposition = res.headers['content-disposition'];
    const filename = contentDisposition?.split('filename=')[1] || 'Template.docx';
    var blob = new Blob([res.data], {
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    });
    fileDownload(blob, filename);
  });
};

// Upload new contract template (HR only)
const uploadContractTemplate = async (file: File) => {
  const url = endpoints.CONTRACT_TEMPLATE;
  const formData = new FormData();
  formData.append("template", file);
  formData.append("role_id", "1"); // HR role_id = 1

  const { data: response } = await axios.post(url, formData, {
    headers: {
      Authorization: accessToken,
      "Content-Type": "multipart/form-data",
    },
  });
  return response;
};

const uploadBulk = async (params: any) => {
  const url = endpoints.CONTRACT + '/upload/bulk';
  const { data: response } = await axios.post(url, params, {
    headers: {
      Authorization: accessToken,
    },
  });

  return response;
};

const getBulkTemplate = async () => {
  const url = endpoints.CONTRACT + '/upload/bulk-template';
  await axios
    .get(url, {
      headers: {
        Authorization: accessToken,
        ContentType:
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      },
      responseType: "arraybuffer",
    }).then((res) => {
      var blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      fileDownload(blob, `bulk_contract_template.xlsx`);
    });
};

const getPublicContractByToken = async (token: string) => {
  const url = endpoints.CONTRACT_PUBLIC_REVIEW(token);
  
  try {
    // Request as JSON with explicit headers to prevent download
    const { data } = await axios.get(url, {
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
    });
    
    // Check if response has the expected structure
    if (data && typeof data === 'object') {
      // Extract data if it's wrapped in an API response
      let contractData = data.data || data;
      
      // Convert relative preview_url to full URL if needed
      if (contractData.preview_url && !contractData.preview_url.startsWith('http') && !contractData.preview_url.startsWith('blob:')) {
        contractData.preview_url = API_URL + contractData.preview_url;
      }
      
      // If preview_url is a DOCX file, wrap it with Google Docs Viewer
      if (contractData.preview_url && contractData.preview_url.toLowerCase().endsWith('.docx')) {
        const encodedUrl = encodeURIComponent(contractData.preview_url);
        contractData.preview_url = `https://docs.google.com/viewer?url=${encodedUrl}&embedded=true`;
      }
      
      return { data: contractData };
    }
  } catch (error: any) {
    // If JSON request fails, try as blob
    try {
      const response = await axios.get(url, {
        responseType: 'blob',
      });

      const blob = response.data;
      const contentType = blob.type || response.headers['content-type'];

      // If response is a PDF or document, create preview URL
      if (
        contentType.includes('application/pdf') ||
        contentType.includes('application/vnd.openxmlformats-officedocument.wordprocessingml.document') ||
        contentType.includes('application/msword') ||
        contentType.includes('text/plain')
      ) {
        const preview_url = URL.createObjectURL(blob);
        
        return {
          data: {
            contract_id: 0,
            name: "Contract Document",
            branch: "",
            placement_location: "",
            contract_start: "",
            contract_end: "",
            status: "employee_review",
            token_used: false,
            preview_url,
          },
        };
      }

      // Try to parse as JSON
      const jsonText = await blob.text();
      const apiResponse = JSON.parse(jsonText);
      let contractData = apiResponse.data || apiResponse;
      
      if (contractData.preview_url && !contractData.preview_url.startsWith('http') && !contractData.preview_url.startsWith('blob:')) {
        contractData.preview_url = API_URL + contractData.preview_url;
      }
      
      // If preview_url is a DOCX file, wrap it with Google Docs Viewer
      if (contractData.preview_url && contractData.preview_url.toLowerCase().endsWith('.docx')) {
        const encodedUrl = encodeURIComponent(contractData.preview_url);
        contractData.preview_url = `https://docs.google.com/viewer?url=${encodedUrl}&embedded=true`;
      }
      
      return { data: contractData };
    } catch (blobError) {
      throw error;
    }
  }
};

const signPublicContract = async (params: { token: string; file: File }) => {
  const url = endpoints.CONTRACT_PUBLIC_SIGN(params.token);
  const formData = new FormData();
  formData.append("file", params.file);

  const { data: response } = await axios.post(url, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  return response;
};

const rejectPublicContract = async (params: {
  token: string;
  rejection_reason: string;
}) => {
  const url = endpoints.CONTRACT_PUBLIC_REJECT(params.token);
  const { data: response } = await axios.post(url, {
    rejection_reason: params.rejection_reason,
  });
  return response;
};

// Resend review link to employee
const resendReviewLink = async (contractId: number) => {
  const url = endpoints.CONTRACT_RESEND_REVIEW_LINK(contractId);
  const { data: response } = await axios.post(url, {}, {
    headers: {
      Authorization: accessToken,
    },
  });
  return response;
};

const service = {
  getAllContracts,
  viewContractDetail,
  createContract,
  deleteContract,
  updateContract,
  updateDraftContract,
  employeeApproveContract,
  employeeRejectContract,
  hrApproveContract,
  hrRejectContract,
  downloadContract,
  respondToContract,
  getEmployeeContracts,
  uploadSignedContract,
  getContractHistory,
  downloadHistoryDocument,
  downloadContractTemplate,
  uploadContractTemplate,
  uploadHrDocument,
  uploadBulk,
  getBulkTemplate,
  getPublicContractByToken,
  signPublicContract,
  rejectPublicContract,
  resendReviewLink,
};

export default service;
